import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  ItemResponse,
  ListResponse,
  PasskeyListItem,
  PasskeyRegisterOptionsResponse,
  TotpSetupResponse,
} from '@proerp/shared';
import { portalRequest } from './apiClient';
import { PORTAL_ME_KEY } from './usePortalUser';

/**
 * Der eigene Zugang im Partnerportal (ADR 0034/0085): Passkeys und
 * Authenticator selbst verwalten, Passwort ändern.
 *
 * Die Endpunkte gab es serverseitig von Anfang an (`PortalAccountController`) —
 * im Portal fehlte nur die Oberfläche dazu. Bis dahin musste ein Partner, der
 * ein neues Telefon hatte, bei uns anrufen; und wer sich beim Erstlogin für
 * TOTP entschieden hatte, kam nie mehr an einen Passkey.
 *
 * Alles hier gilt ausschließlich für den **eigenen** Zugang: Die Nutzer-ID
 * kommt serverseitig aus dem Token, nie aus einem Body — es gibt hier gar
 * keinen Parameter, mit dem man auf einen fremden Zugang zeigen könnte.
 */

export const PORTAL_PASSKEYS_KEY = ['portal', 'passkeys'] as const;

export function usePortalPasskeys(options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: PORTAL_PASSKEYS_KEY,
    queryFn: async () => {
      const response = await portalRequest<ListResponse<PasskeyListItem>>('/auth/passkey');
      return response.data;
    },
    enabled: options.enabled ?? true,
    retry: false,
  });
}

/** Schritt 1 der Passkey-Registrierung: Challenge vom Server holen. */
export function useRegisterPortalPasskeyOptions() {
  return useMutation({
    mutationFn: () =>
      portalRequest<ItemResponse<PasskeyRegisterOptionsResponse>>('/auth/passkey/register-options', {
        method: 'POST',
      }),
  });
}

/** Schritt 2: die Antwort des Geräts bestätigen lassen. */
export function useRegisterPortalPasskey() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      challengeToken: string;
      attestationResponse: unknown;
      deviceLabel: string;
    }) =>
      portalRequest<void>('/auth/passkey/register', {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: PORTAL_PASSKEYS_KEY }),
  });
}

export function useDeletePortalPasskey() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      portalRequest<void>(`/auth/passkey/${id}`, { method: 'DELETE' }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: PORTAL_PASSKEYS_KEY }),
  });
}

/** Authenticator nachrüsten, Schritt 1: Geheimnis + QR-Code. */
export function useEnrollPortalTotpOptions() {
  return useMutation({
    mutationFn: () =>
      portalRequest<ItemResponse<TotpSetupResponse>>('/auth/totp/enroll-options', {
        method: 'POST',
      }),
  });
}

/** Schritt 2: mit einem Code aus der App bestätigen. */
export function useConfirmPortalTotp() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (code: string) =>
      portalRequest<void>('/auth/totp/enroll', { method: 'POST', body: JSON.stringify({ code }) }),
    // `me` trägt `isTotpEnabled` — ohne das Nachladen bliebe die Seite
    // behaupten, es gäbe keinen Authenticator.
    onSuccess: () => queryClient.invalidateQueries({ queryKey: PORTAL_ME_KEY }),
  });
}

export function useRemovePortalTotp() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => portalRequest<void>('/auth/totp', { method: 'DELETE' }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: PORTAL_ME_KEY }),
  });
}

export function useChangePortalPassword() {
  return useMutation({
    mutationFn: (input: { currentPassword: string; newPassword: string }) =>
      portalRequest<void>('/auth/change-password', {
        method: 'POST',
        body: JSON.stringify(input),
      }),
  });
}
